import { Database, supabase } from "../../backend/supabase";
import { TemplateType } from "./analyzeTemplates";

// Define the template types that are considered "allowed" which should NOT be hidden/deleted.
const ALLOWED_TEMPLATES: (TemplateType | null | undefined | '')[] = [
    'phaser_react',
    null,
    undefined,
    '',
];

type GameRow = Database['public']['Tables']['games']['Row'];

/**
 * Fetches all game IDs and their 'template' type in batches, and updates
 * the 'hidden' or 'deleted' column for games whose template is not in ALLOWED_TEMPLATES.
 * Uses cursor-based pagination (keyset pagination) for stable, efficient iteration.
 * * @param action 'hide' to set 'hidden' to true, or 'delete' to set 'deleted' to true.
 * @param onProgress Optional callback for progress reporting.
 * @returns A promise that resolves to an object containing the success status and the count of updated rows.
 */
export async function removeOldTemplates(
    action: 'hide' | 'delete',
    onProgress?: (current: number, total: number) => void
): Promise<{ success: boolean, updatedCount: number }> {
    const GAME_ID_PAGE_SIZE = 100;
    const TARGET_COLUMN = action === 'hide' ? 'hidden' : 'deleted';

    console.log(`Starting to ${action} old templates...`);

    // Step 1: Paginate and fetch ALL Game IDs and their template, using keyset pagination.
    let gamesToUpdate: { id: string, template: TemplateType | null }[] = [];
    let lastId: string | null = null;
    let lastUpdatedAt: string | null = null;
    let hasMore = true;

    // First, get the exact count as seen by the RLS policy for progress estimation
    const { count: initialCount, error: countError } = await supabase
        .from('games')
        .select(`id`, { count: 'exact', head: true });

    if (countError) {
        console.error('Error fetching initial game count:', countError);
        throw countError;
    }
    const totalCount = initialCount || 0;
    console.log(`Initial total game count reported by client: ${totalCount}`);


    // Start pagination loop to identify games for update
    while (hasMore) {
        let query = supabase
            .from('games')
            .select(`id, updated_at, template`) // Select updated_at for stable keyset
            .order('updated_at', { ascending: true }) // Order by time first
            .order('id', { ascending: true }) // Then by ID as tie breaker
            .limit(GAME_ID_PAGE_SIZE);

        if (lastUpdatedAt && lastId) {
            query = query.or(`updated_at.gt.${lastUpdatedAt},and(updated_at.eq.${lastUpdatedAt},id.gt.${lastId})`);
        }

        const { data: pageGames, error: pageGamesError } = await query;

        if (pageGamesError) {
            console.error('Error fetching game page:', pageGamesError);
            throw pageGamesError;
        }

        if (!pageGames || pageGames.length === 0) {
            hasMore = false;
        } else {
            // Identify games with a template NOT in the allowed list
            const pageGamesToUpdate = pageGames
                .filter(game => {
                    const template = game.template as TemplateType | null | undefined | '';
                    // The '!' is the key logic: we want games whose template is *not* in the allowed list.
                    return !ALLOWED_TEMPLATES.includes(template); 
                })
                .map(game => ({
                    id: game.id, 
                    template: game.template as TemplateType | null 
                }));

            gamesToUpdate = gamesToUpdate.concat(pageGamesToUpdate);
            
            const lastGame = pageGames[pageGames.length - 1];
            lastId = lastGame.id;
            lastUpdatedAt = lastGame.updated_at;

            if (pageGames.length < GAME_ID_PAGE_SIZE) {
                hasMore = false;
            }
            
            // Report progress on game fetching
            if (onProgress) {
                const totalFetchedSoFar = gamesToUpdate.length; // We don't have the original batch count
                onProgress(totalFetchedSoFar, totalCount); 
            }
        }
    }
    
    // Step 2: Perform the bulk update for the identified games
    const totalUpdates = gamesToUpdate.length;
    let updatedCount = 0;
    const UPDATE_BATCH_SIZE = 1000;
    
    if (totalUpdates === 0) {
        console.log('No games found to update.');
        return { success: true, updatedCount: 0 };
    }
    
    console.log(`Found ${totalUpdates} games with old templates to ${action}. (Using bulk_update_games RPC)`);

    for (let i = 0; i < totalUpdates; i += UPDATE_BATCH_SIZE) {
        const batchUpdates = gamesToUpdate.slice(i, i + UPDATE_BATCH_SIZE).map(game => ({
            id: game.id,
            [TARGET_COLUMN]: true, // Set 'hidden' or 'deleted' to true
            // NO explicit updated_at here, so the trigger will preserve OLD.updated_at
        }));
        
        // Use the custom RPC to perform updates while preserving the timestamp
        const { error } = await supabase.rpc('bulk_update_games', {
            updates: batchUpdates
        });
            
        if (error) {
            console.error(`Error ${action}ing templates for batch starting at index ${i}:`, error);
            // We can continue to the next batch even if one fails
        } else {
            updatedCount += batchUpdates.length; // Count all as successful if RPC succeeds
            console.log(`Successfully processed batch ${Math.floor(i / UPDATE_BATCH_SIZE) + 1}/${Math.ceil(totalUpdates / UPDATE_BATCH_SIZE)}. Updated so far: ${updatedCount}`);
        }
    }
    
    const success = updatedCount === totalUpdates;
    console.log(`${action} finished. Total games targeted: ${totalUpdates}. Successfully updated: ${updatedCount}. Success: ${success}`);
    
    return { success, updatedCount };
}